/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const UC_CHEVRON = {
  prev: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  next: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
};

class CarouselSlider extends HTMLElement {
  constructor() {
    super();
    // Get slides, ignoring nested .slider__item elements
    const firstSlide = this.querySelector('.slider__item');
    if (!firstSlide) return;

    this.slides = firstSlide.parentElement.children;
    if (this.slides.length < 2) return;

    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.slider = this.querySelector('.slider');
    this.grid = this.querySelector('.slider__grid');
    if (!this.slider || !this.grid) return;

    this.rtl = document.dir === 'rtl';
    this.nav = this.buildNav();

    if (this.nav) {
      this.prevBtn = this.nav.querySelector('button[name="prev"]');
      this.nextBtn = this.nav.querySelector('button[name="next"]');
    }

    this.initSlider();
    window.addEventListener('on:breakpoint-change', this.handleBreakpointChange.bind(this));
  }

  /**
   * Builds the universal nav (see universal-carousel.css) so every carousel is
   * navigated the same way, whatever markup the section shipped with. Any nav
   * already in the section is reused as the container and rewritten.
   * @returns {Element|null}
   */
  buildNav() {
    if (this.dataset.uc === 'false') {
      return this.querySelector(`.slider-nav__btn[aria-controls='${this.slider.id}']`)?.closest('.slider-nav') || null;
    }

    let nav = this.querySelector('.uc-nav');

    if (!nav) {
      const strings = window.theme?.strings || {};
      nav = this.querySelector('.slider-nav') || document.createElement('div');
      nav.className = 'uc-nav';
      nav.innerHTML = `
        <button type="button" class="uc-nav__btn uc-nav__btn--prev uc-nav__btn--hidden" name="prev" aria-controls="${this.slider.id}" aria-label="${strings.sliderPrev || 'Previous'}">${UC_CHEVRON.prev}</button>
        <button type="button" class="uc-nav__btn uc-nav__btn--next" name="next" aria-controls="${this.slider.id}" aria-label="${strings.sliderNext || 'Next'}">${UC_CHEVRON.next}</button>`;
    }

    // The arrows overlay the track, so they have to sit outside the scrolling
    // element (otherwise they scroll away and get clipped) in a positioned box.
    const holder = this.slider.parentElement || this;
    if (nav.parentElement !== holder) holder.appendChild(nav);
    if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';

    return nav;
  }

  initSlider() {
    this.gridWidth = this.grid.clientWidth;

    // Distance between leading edges of adjacent slides (i.e. width of card + gap).
    this.slideSpan = this.getWindowOffset(this.slides[1]) - this.getWindowOffset(this.slides[0]);

    // Width of gap between slides.
    this.slideGap = this.slideSpan - this.slides[0].clientWidth;

    this.slidesPerPage = Math.round((this.gridWidth + this.slideGap) / this.slideSpan);
    this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1;
    this.totalPages = this.slides.length - this.slidesPerPage + 1;

    this.setCarouselState(this.totalPages > 1);
    if (this.totalPages < 2 || !this.nav) return;

    this.sliderStart = this.getWindowOffset(this.slider);
    if (!this.sliderStart) this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
    this.sliderEnd = this.sliderStart + this.gridWidth;

    // Remove reveal transitions from off-screen slides
    if (!this.dataset.keepAnimations && this.grid.querySelector('[data-cc-animate]')) {
      for (let i = this.slidesPerPage; i < this.slides.length; i += 1) {
        this.slides[i].querySelectorAll('[data-cc-animate]').forEach((el) => {
          el.removeAttribute('data-cc-animate-delay');
          el.classList.add('cc-animate-in');
        });
      }
    }

    this.addListeners();
    this.setButtonStates();
  }

  addListeners() {
    // init() is re-run by sections that swap their markup in (mega content),
    // so drop any previous binding rather than stacking a second one.
    if (this.scrollHandler) this.removeListeners();

    // Short debounce so the arrows drop in and out while the carousel is being
    // free-scrolled, rather than a beat after it stops.
    this.scrollHandler = debounce(this.handleScroll.bind(this), 100);
    this.navClickHandler = this.handleNavClick.bind(this);

    this.slider.addEventListener('scroll', this.scrollHandler);
    this.nav.addEventListener('click', this.navClickHandler);
  }

  removeListeners() {
    this.slider.removeEventListener('scroll', this.scrollHandler);
    this.nav.removeEventListener('click', this.navClickHandler);
  }

  /**
   * Handles 'scroll' events on the slider element.
   */
  handleScroll() {
    this.currentIndex = Math.round(this.slider.scrollLeft / this.slideSpan);
    this.setButtonStates();
  }

  /**
   * Handles 'click' events on the nav buttons container.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    // closest(), not matches(), so a click landing on the chevron still counts.
    const btn = evt.target.closest('button[name="prev"], button[name="next"]');
    if (!btn || !this.nav.contains(btn)) return;

    if ((btn.name === 'next' && !this.rtl) || (btn.name === 'prev' && this.rtl)) {
      this.scrollPos = this.slider.scrollLeft + (this.slidesToScroll * this.slideSpan);
    } else {
      this.scrollPos = this.slider.scrollLeft - (this.slidesToScroll * this.slideSpan);
    }

    this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
  }

  /**
   * Handles 'on:breakpoint-change' events on the window.
   */
  handleBreakpointChange() {
    if (this.nav) this.removeListeners();
    this.initSlider();
  }

  /**
   * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
   * @param {number} el - Element.
   * @returns {number}
   */
  getWindowOffset(el) {
    return this.rtl
      ? window.innerWidth - el.getBoundingClientRect().right
      : el.getBoundingClientRect().left;
  }

  /**
   * Gets the visible state of a slide.
   * @param {Element} el - Slide element.
   * @returns {boolean}
   */
  getSlideVisibility(el) {
    const slideStart = this.getWindowOffset(el);
    const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
    return slideStart >= this.sliderStart && slideEnd <= Math.ceil(this.sliderEnd);
  }

  /**
   * Sets the active state of the carousel.
   * @param {boolean} active - Set carousel as active.
   */
  setCarouselState(active) {
    if (active) {
      this.removeAttribute('inactive');

      // If slider width changed when activated, reinitialise it.
      if (this.gridWidth !== this.grid.clientWidth) {
        this.handleBreakpointChange();
      }
    } else {
      this.setAttribute('inactive', '');
    }
  }

  /**
   * Sets the disabled state of the nav buttons.
   */
  setButtonStates() {
    if (!this.prevBtn || !this.nextBtn) return;

    // Math.abs() because scrollLeft runs negative in rtl, and the tolerance
    // covers the fractional scroll positions free scrolling lands on.
    const atStart = this.getSlideVisibility(this.slides[0]) && Math.abs(this.slider.scrollLeft) <= 2;
    const atEnd = this.getSlideVisibility(this.slides[this.slides.length - 1]);

    this.prevBtn.disabled = atStart;
    this.nextBtn.disabled = atEnd;
    this.prevBtn.classList.toggle('uc-nav__btn--hidden', atStart);
    this.nextBtn.classList.toggle('uc-nav__btn--hidden', atEnd);
  }
}

customElements.define('carousel-slider', CarouselSlider);
